import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from "recharts";
import { TarefaStatus } from "@workflow/types";

import { PeriodoResumoAdminTarefas } from "@/types/tarefa.type";
import { Skeleton } from "@/components/ui/skeleton";
import { useTarefaAdminResumoPeriodo } from "@/hooks/use-tarefa-admin-resumo-periodo";

import { TAREFA_STATUS_CHART_CONFIG } from "./tarefa-status-visual";

const STATUS_ORDER = [
  TarefaStatus.PENDENTE,
  TarefaStatus.EM_EXECUCAO,
  TarefaStatus.CONCLUIDO,
  TarefaStatus.CANCELADO,
  TarefaStatus.ARQUIVADO,
] as const;

function DonutTooltip({ active, payload }: Readonly<TooltipProps<number, string>>) {
  const item = payload?.[0];
  if (!active || !item) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-md">
      <p className="font-medium text-foreground">{item.name}</p>
      <p className="text-muted-foreground">{item.value} tarefas</p>
    </div>
  );
}

export function TarefaStatusDonutCard({ dias }: { dias: PeriodoResumoAdminTarefas }) {
  const { resumo, loading } = useTarefaAdminResumoPeriodo(dias);

  const data = useMemo(() => {
    const porStatus = new Map<TarefaStatus, number>();
    for (const row of resumo.criadasPorDia) {
      porStatus.set(row.status, (porStatus.get(row.status) ?? 0) + row.total);
    }
    return STATUS_ORDER.map((status) => ({
      status,
      label: TAREFA_STATUS_CHART_CONFIG[status].label,
      color: TAREFA_STATUS_CHART_CONFIG[status].color,
      value: porStatus.get(status) ?? 0,
    })).filter((d) => d.value > 0);
  }, [resumo]);

  const total = data.reduce((soma, d) => soma + d.value, 0);

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-foreground">Distribuição por status</p>

      {loading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : total === 0 ? (
        <p className="flex h-40 items-center justify-center text-center text-sm italic text-muted-foreground">
          Nenhuma tarefa criada no período.
        </p>
      ) : (
        <div className="flex min-h-40 flex-col items-center gap-4 sm:flex-row">
          <div className="relative h-40 w-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={48}
                  outerRadius={68}
                  paddingAngle={2}
                  strokeWidth={0}
                >
                  {data.map((entry) => (
                    <Cell key={entry.status} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={<DonutTooltip />}
                  wrapperStyle={{ zIndex: 50, outline: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold tabular-nums text-foreground">{total}</span>
              <span className="text-[11px] text-muted-foreground">tarefas</span>
            </div>
          </div>

          <ul className="w-full flex-1 space-y-1.5">
            {data.map((item) => (
              <li key={item.status} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
                <span className="font-semibold tabular-nums text-foreground">{item.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
